import React from 'react'
import ControlledUser from 'components/ControlledUser'
import KanBoIcon from 'components/KanBoIcon'
import { FormattedMessage } from 'components/Intl'
import { useColor } from 'utils/colors'
import { useSelector } from 'react-redux'
import { getStatusIconName, getStatusFormattedMessageId } from 'utils/status'
import moment from 'moment'
import { convertFromToUser } from 'utils/users'

export type StartDateFilterRuleType = 'withDate' | 'overdue' | 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'later'

export const startDateFilterRules: {
  [k in StartDateFilterRuleType]: {
    formattedMessageId: string;
    test: (rule, startDate?: number) => boolean;
  };
} = {
  'withDate': {
    formattedMessageId: 'FILTER_BY_START_DATE_WITH_DATE',
    test: (rule, startDate) => Boolean(startDate),
  },
  'overdue': {
    formattedMessageId: 'FILTER_BY_START_DATE_OVERDUE',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isBefore(moment())),
  },
  'today': {
    formattedMessageId: 'FILTER_BY_START_DATE_TODAY',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isSame(moment(), 'day')),
  },
  'tomorrow': {
    formattedMessageId: 'FILTER_BY_START_DATE_TOMORROW',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isSame(moment().add(1, 'day'), 'day')),
  },
  'thisWeek': {
    formattedMessageId: 'FILTER_BY_START_DATE_THIS_WEEK',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isSame(moment(), 'week')),
  },
  'nextWeek': {
    formattedMessageId: 'FILTER_BY_START_DATE_NEXT_WEEK',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isSame(moment().add(1, 'week'), 'week')),
  },
  'later': {
    formattedMessageId: 'FILTER_BY_START_DATE_LATER',
    test: (rule, startDate) => Boolean(startDate && moment(startDate).isSameOrAfter(moment().add(2, 'week'), 'week')),
  },
}

export type DueDateFilterRuleType = 'withDate' | 'overdue' | 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'later'

export const dueDateFilterRules: {
  [k in DueDateFilterRuleType]: {
    formattedMessageId: string;
    test: (rule, startDate?: number) => boolean;
  };
} = {
  'withDate': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_WITH_DATE',
    test: (rule, dueDate) => Boolean(dueDate),
  },
  'overdue': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_OVERDUE',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isBefore(moment())),
  },
  'today': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_TODAY',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isSame(moment(), 'day')),
  },
  'tomorrow': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_TOMORROW',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isSame(moment().add(1, 'day'), 'day')),
  },
  'thisWeek': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_THIS_WEEK',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isSame(moment(), 'week')),
  },
  'nextWeek': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_NEXT_WEEK',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isSame(moment().add(1, 'week'), 'week')),
  },
  'later': {
    formattedMessageId: 'FILTER_BY_DUE_DATE_LATER',
    test: (rule, dueDate) => Boolean(dueDate && moment(dueDate).isSameOrAfter(moment().add(2, 'week'), 'week')),
  },
}

export type CardRelationFilterRuleType = 'parent' | 'predecessor' | 'successor' | 'child'

export const cardRelationFilterRules: {
  [k in CardRelationFilterRuleType]: {
    formattedMessageId: string;
    iconName: string;
    test: (cardRelations, boardCardIds: number[]) => boolean;
  };
} = {
  'parent': {
    formattedMessageId: 'FILTER_BY_CARD_RELATIONS_WITH_PARENTS',
    iconName: 'kanbo-16-relation-parent',
    test: (cardRelations, boardCardIds) => cardRelations?.parentIds?.some(cardId => boardCardIds.includes(cardId.toString())),
  },
  'predecessor': {
    formattedMessageId: 'FILTER_BY_CARD_RELATIONS_WITH_PREDECESSORS',
    iconName: 'kanbo-16-relation-predecessor',
    test: (cardRelations, boardCardIds) => cardRelations?.predecessorIds?.some(cardId => boardCardIds.includes(cardId.toString())),
  },
  'successor': {
    formattedMessageId: 'FILTER_BY_CARD_RELATIONS_WITH_SUCCESSORS',
    iconName: 'kanbo-16-relation-successor',
    test: (cardRelations, boardCardIds) => cardRelations?.successorIds?.some(cardId => boardCardIds.includes(cardId.toString())),
  },
  'child': {
    formattedMessageId: 'FILTER_BY_CARD_RELATIONS_WITH_CHILDREN',
    iconName: 'kanbo-16-relation-child',
    test: (cardRelations, boardCardIds) => cardRelations?.childIds?.some(cardId => boardCardIds.includes(cardId.toString())),
  },
}

const StatusIcon = ({ statusId, ...props }) => {
  const color = useSelector(state => state['view-board'].cardStatuses.byId[statusId]?.color)
  const role = useSelector(state => state['view-board'].cardStatuses.byId[statusId]?.role)

  const statusColor = useColor(color)

  return role ? (
    <KanBoIcon
      {...props}
      name={getStatusIconName(role)}
      style={{
        color: statusColor,
      }}
    />
  ) : (
    <KanBoIcon
      {...props}
      name='kanbo-16-in-process'
    />
  )
}

const StatusName = ({ statusId }) => {
  return useSelector(state => state['view-board'].cardStatuses.byId[statusId]?.name) || <span className='text-danger'><FormattedMessage id='FILTER_BY_CARD_STATUS_UNKNOWN_CARD_STATUS' /></span>
}

const LabelIcon = ({ labelId, ...props }) => {
  const color = useSelector(state => state['view-board'].labels.labelsById[labelId]?.color)

  const labelColor = useColor(color)

  return (
    <KanBoIcon
      {...props}
      name='kanbo-16-label'
      style={labelId !== true ? {
        color: labelColor,
      } : undefined}
    />
  )
}

const LabelName = ({ labelId }) => {
  const name = useSelector(state => state['view-board'].labels.labelsById[labelId]?.name) || <span className='text-danger'><FormattedMessage id='FILTER_BY_LABELS_UNKNOWN_LABEL' /></span>

  return labelId !== true ? name : <FormattedMessage id='FILTER_BY_LABELS_WITH_LABELS' />
}

const ListIcon = ({ listId, ...props }) => {
  const color = useSelector(state => state['view-board'].lists[listId]?.settings.color)

  const listColor = useColor(color)

  return (
    <KanBoIcon
      {...props}
      name='kanbo-16-list'
      style={listId !== true ? {
        color: listColor,
      } : undefined}
    />
  )
}


const ListName = ({ listId }) => {
  const name = useSelector(state => state['view-board'].lists[listId]?.name) || <span className='text-danger'><FormattedMessage id='FILTER_BY_LIST_UNKNOWN_LIST' /></span>

  return listId !== true ? name : <FormattedMessage id='FILTER_BY_LIST_WITH_LIST' />
}

export type CardFilterRuleType =
  'name' |
  'user' |
  'statusRole' |
  'status' |
  'label' |
  'startDate' |
  'dueDate' |
  'cardRelation' |
  'isBlocked' |
  'createdBy' |
  'responsible' |
  'coworker' |
  'list' |
  'issue' |
  'isObserved'

type CardFilterRule = {
  getKey: (rule: any) => string;
  test: (rule: any, card: any, boardState: any) => boolean;
  labelIcon?: (rule: any) => React.ReactNode;
  labelTitle?: (rule: any) => React.ReactNode;
}

const cardFilterRule: {
  [k in CardFilterRuleType]: CardFilterRule;
} = {
  'name': {
    getKey: () => 'name',
    labelIcon: () => <KanBoIcon name='kanbo-16-letter-count' />,
    labelTitle: ({ name }) => name,
    test: (rule, card) => card.name.toLocaleLowerCase().indexOf(rule.name.toLocaleLowerCase()) !== -1,
  },
  'user': {
    getKey: ({ userId }) => `user-${userId}`,
    labelIcon: ({ userId }) => userId === true ? <KanBoIcon name='kanbo-16-members' /> : <ControlledUser id={userId} avatar size={20} />,
    labelTitle: ({ userId }) => (
      userId === true ? (
        <FormattedMessage id='FILTER_BY_USERS_WITH_USERS' />   
      ) : (
        <ControlledUser id={userId} text />
      )
    ),
    test: (rule, card) => {
      const cardUsers =
        card.responsible
          ? [card.responsible, ...card.users]
          : card.users

      if (rule.userId === true) {
        return cardUsers.length
      }

      return cardUsers.includes(rule.userId)
    },
  },
  'statusRole': {
    getKey: ({ roleId }) => `statusRole-${roleId}`,
    labelIcon: ({ roleId }) => <KanBoIcon name={getStatusIconName(roleId)} />,
    labelTitle: ({ roleId }) => <FormattedMessage id={getStatusFormattedMessageId(roleId)} />,
    test: (rule, card, { cardStatusesById }) => cardStatusesById[card.statusId]?.role === rule.roleId,
  },
  'status': {
    getKey: ({ statusId }) => `status-${statusId}`,
    labelIcon: ({ statusId }) => <StatusIcon statusId={statusId} />,
    labelTitle: ({ statusId }) => <StatusName statusId={statusId} />,
    test: (rule, card) => card.statusId === rule.statusId,
  },
  'label': {
    getKey: ({ labelId }) => `label-${labelId}`,
    labelIcon: ({ labelId }) => <LabelIcon labelId={labelId} />,
    labelTitle: ({ labelId }) => <LabelName labelId={labelId} />,
    test: (rule, card) =>
      rule.labelId === true
        ? Boolean(card.labels.length)
        : card.labels.some(labelId => labelId === rule.labelId),
  },
  'startDate': {
    getKey: (rule) => `startDate${!startDateFilterRules[rule.dateType] ? `-${rule.dateType}` : ''}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-start-date' />,
    labelTitle: ({ dateType, date }) => {
      if (startDateFilterRules[dateType]) {
        return (
          <FormattedMessage id={startDateFilterRules[dateType].formattedMessageId} />
        )
      }

      if (dateType === 'beforeDate') {
        return (
          <FormattedMessage
            id='FILTER_BY_START_DATE_BEFORE_DATE'
            values={{
              date: date && moment(date).format('LL')
            }}
          />
        )
      }

      if (dateType === 'afterDate') {
        return (
          <FormattedMessage
            id='FILTER_BY_START_DATE_AFTER_DATE'
            values={{
              date: date && moment(date).format('LL')
            }}
          />
        )
      }
    },
    test: (rule, card) => {
      if (startDateFilterRules[rule.dateType]) {
        return startDateFilterRules[rule.dateType].test(rule, card.dueDate)
      }

      if (rule.dateType === 'beforeDate') {
        return Boolean(card.startDate && moment(card.startDate).isSameOrBefore(moment(rule.date).endOf('day')))
      }

      if (rule.dateType === 'afterDate') {
        return Boolean(card.startDate && moment(card.startDate).isSameOrAfter(moment(rule.date).startOf('day')))
      }
    },
  },
  'dueDate': {
    getKey: (rule) => `dueDate${!dueDateFilterRules[rule.dateType] ? `-${rule.dateType}` : ''}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-due-date' />,
    labelTitle: ({ dateType, date }) => {
      if (dueDateFilterRules[dateType]) {
        return (
          <FormattedMessage id={dueDateFilterRules[dateType].formattedMessageId} />
        )
      }

      if (dateType === 'beforeDate') {
        return (
          <FormattedMessage
            id='FILTER_BY_DUE_DATE_BEFORE_DATE'
            values={{
              date: date && moment(date).format('LL')
            }}
          />
        )
      }

      if (dateType === 'afterDate') {
        return (
          <FormattedMessage
            id='FILTER_BY_DUE_DATE_AFTER_DATE'
            values={{
              date: date && moment(date).format('LL')
            }}
          />
        )
      }
    },
    test: (rule, card) => {
      if (dueDateFilterRules[rule.dateType]) {
        return dueDateFilterRules[rule.dateType].test(rule, card.dueDate)
      }

      if (rule.dateType === 'beforeDate') {
        return Boolean(card.dueDate && moment(card.dueDate).isSameOrBefore(moment(rule.date).endOf('day')))
      }

      if (rule.dateType === 'afterDate') {
        return Boolean(card.dueDate && moment(card.dueDate).isSameOrAfter(moment(rule.date).startOf('day')))
      }
    },
  },
  'cardRelation': {
    getKey: ({ relationType } : { relationType: CardRelationFilterRuleType }) => `cardRelation-${relationType}`,
    labelIcon: ({ relationType } : { relationType: CardRelationFilterRuleType }) => <KanBoIcon name={cardRelationFilterRules[relationType].iconName} />,
    labelTitle: ({ relationType } : { relationType: CardRelationFilterRuleType }) => <FormattedMessage id={cardRelationFilterRules[relationType].formattedMessageId} />,
    test: ({ relationType } : { relationType: CardRelationFilterRuleType }, card, { cardRelationsById, boardCardIds }) =>
      cardRelationFilterRules[relationType].test(cardRelationsById[card.id], boardCardIds),
  },
  'isBlocked': {
    getKey: () => `isBlocked`,
    labelIcon: () => <KanBoIcon name='kanbo-16-block' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_IS_BLOCKED' />,
    test: (rule, card) => card.blockerCount > 0,
  },
  'createdBy': {
    getKey: ({ userId }) => `createdBy-${userId}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-members' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_CREATED_BY_CURRENT_USER' />,
    test: (rule, card, { currentUserId }) => {
      const user = convertFromToUser(card.createdBy)

      return (
        user.type === 'USER' &&
        user.id === currentUserId
      )
    },
  },
  'responsible': {
    getKey: ({ userId }) => `responsible-${userId}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-members' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_RESPONSIBLE_CURRENT_USER' />,
    test: (rule, card, { currentUserId }) => card.responsible === currentUserId,
  },
  'coworker': {
    getKey: ({ userId }) => `coworker-${userId}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-members' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_COWORKER_CURRENT_USER' />,
    test: (rule, card, { currentUserId }) => card.users.includes(currentUserId),
  },
  'list': {
    getKey: ({ listId }) => `list-${listId}`,
    labelIcon: ({ listId }) => <ListIcon listId={listId} />,
    labelTitle: ({ listId }) => <ListName listId={listId} />,
    test: (rule, card) =>
      rule.listId === true
        ? Boolean(card.listId)
        : card.listId === rule.listId,
  },
  'issue': {
    getKey: ({ issueLevel }) => `issue-${issueLevel}`,
    labelIcon: () => <KanBoIcon name='kanbo-16-exclamation' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_ISSUE_WITH_ISSUE' />,
    test: (rule, card) => Boolean(card.issueLevel)
  },
  'isObserved': {
    getKey: () => `isObserved`,
    labelIcon: () => <KanBoIcon name='kanbo-16-follow' />,
    labelTitle: () => <FormattedMessage id='FILTER_BY_IS_OBSERVED' />,
    test: (rule, card, { observedTags }) => observedTags.some(tag => tag === `card-${card.id}`),
  },
}

export default cardFilterRule